import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Paper, TextField, MenuItem, Select, InputLabel, FormControl, Button, IconButton, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import amexTransactions from '../services/amex_transactions.service';

export default function CardManagerPage() {
    const [transactions, setTransactions] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedCard, setSelectedCard] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [users, setUsers] = useState([]);
    const [cards, setCards] = useState([]);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [newNotes, setNewNotes] = useState('');
    const [openModal, setOpenModal] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await amexTransactions.getUniqueUsers();
                if (response.success) {
                    setUsers(response.result);
                } else {
                    console.error("Error fetching users:", response.message);
                }
            } catch (error) {
                console.error("Failed to fetch users:", error);
            }
        };

        const fetchCards = async () => {
            try {
                const response = await amexTransactions.getUniqueCards();
                if (response.success) {
                    setCards(response.result);
                } else {
                    console.error("Error fetching cards:", response.message);
                }
            } catch (error) {
                console.error("Failed to fetch cards:", error);
            }
        };

        fetchCards();
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchTransactions(page + 1, rowsPerPage, searchKeyword, selectedUser, selectedCard, startDate, endDate);
    }, [page, rowsPerPage, searchKeyword, selectedUser, selectedCard, startDate, endDate]);

    const fetchTransactions = async (page, pageSize, keyword, user_id, card, start, end) => {
        try {
            const response = await amexTransactions.getTransactions(page, pageSize, keyword, user_id, card, start, end);
            if (response.success) {
                setTransactions(response.result.transactions);
                setTotalCount(response.result.totalCount);
            } else {
                console.error("Error fetching transactions:", response.message);
            }
        } catch (error) {
            console.error("Failed to fetch transactions:", error);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchKeyword(event.target.value);
    };

    const handleUserChange = (event) => {
        setSelectedUser(event.target.value);
    };

    const handleCardChange = (event) => {
        setSelectedCard(event.target.value);
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
    };

    const handleExport = async () => {
        try {
            const response = await amexTransactions.getAmexTransactionExport(1, 1000, searchKeyword, 'date', 'desc', startDate, endDate, 'excel');
            const downloadUrl = window.URL.createObjectURL(response);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'transactions.xlsx';
            link.click();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("Failed to export transactions:", error);
        }
    };

    const handleEditNotes = (transaction) => {
        setSelectedTransaction(transaction);
        setNewNotes(transaction.notes || '');  // Set the current notes or empty if not available
        setOpenModal(true);
    };

    const handleSaveNotes = async () => {
        if (selectedTransaction && newNotes !== selectedTransaction.notes) {
            try {
                const response = await amexTransactions.updateTransactionNotes(selectedTransaction.id, newNotes);
                if (response.success) {
                    setTransactions(transactions.map((transaction) =>
                        transaction.id === selectedTransaction.id
                            ? { ...transaction, notes: newNotes }
                            : transaction
                    ));
                    setOpenModal(false);
                } else {
                    console.error("Error updating transaction notes:", response.message);
                }
            } catch (error) {
                console.error("Error saving notes:", error);
            }
        } else {
            setOpenModal(false);
        }
    };

    const handleCancelNotes = () => {
        setOpenModal(false);
    };

    return (
        <div style={{ marginTop: "20px", width: "80%", margin: "auto" }}>
            <h1>CARD BALANCE MANAGER</h1>
            <div style={{ marginBottom: "20px" }}>
                <TextField
                    label="Search"
                    variant="outlined"
                    value={searchKeyword}
                    onChange={handleSearchChange}
                    style={{ marginRight: "20px", width: "200px" }}
                />
                <FormControl variant="outlined" style={{ marginRight: "20px", minWidth: 150 }}>
                    <InputLabel>User</InputLabel>
                    <Select
                        value={selectedUser}
                        onChange={handleUserChange}
                        label="User"
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {users.map((user, index) => (
                            <MenuItem key={index} value={user}>{user}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="outlined" style={{ marginRight: "20px", minWidth: 150 }}>
                    <InputLabel>Card</InputLabel>
                    <Select
                        value={selectedCard}
                        onChange={handleCardChange}
                        label="Card"
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {cards.map((card, index) => (
                            <MenuItem key={index} value={card}>{card}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Start Date"
                    type="date"
                    variant="outlined"
                    value={startDate}
                    onChange={handleStartDateChange}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    style={{ marginRight: "20px", minWidth: 150 }}
                />
                <TextField
                    label="End Date"
                    type="date"
                    variant="outlined"
                    value={endDate}
                    onChange={handleEndDateChange}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    style={{ minWidth: 150 }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    style={{ marginLeft: "20px", height: 54 }}
                    onClick={handleExport}
                >
                    Export to Excel
                </Button>
            </div>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Receipt</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Card Member</TableCell>
                            <TableCell>Account Number</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Extended Details</TableCell>
                            <TableCell>Appears On Statement As</TableCell>
                            <TableCell>Address</TableCell>
                            <TableCell>City/State</TableCell>
                            <TableCell>Zip Code</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>Reference</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Notes</TableCell> {/* New Column for Notes */}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions.map((transaction) => (
                            <TableRow key={transaction.reference}>
                                <TableCell>{transaction.date}</TableCell>
                                <TableCell>{transaction.receipt}</TableCell>
                                <TableCell>{transaction.description}</TableCell>
                                <TableCell>{transaction.cardMember}</TableCell>
                                <TableCell>{transaction.accountNumber}</TableCell>
                                <TableCell>{transaction.amount}</TableCell>
                                <TableCell>{transaction.extendedDetails}</TableCell>
                                <TableCell>{transaction.appearsOnStatementAs}</TableCell>
                                <TableCell>{transaction.address}</TableCell>
                                <TableCell>{transaction.cityState}</TableCell>
                                <TableCell>{transaction.zipCode}</TableCell>
                                <TableCell>{transaction.country}</TableCell>
                                <TableCell>{transaction.reference}</TableCell>
                                <TableCell>{transaction.category}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleEditNotes(transaction)}>
                                        <EditIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Modal for editing notes */}
            <Dialog open={openModal} onClose={handleCancelNotes}>
                <DialogTitle>Edit Notes</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Notes"
                        variant="outlined"
                        multiline
                        fullWidth
                        value={newNotes}
                        onChange={(e) => setNewNotes(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelNotes} color="secondary">Cancel</Button>
                    <Button onClick={handleSaveNotes} color="primary">Save</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
